import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { Box, Typography } from "@mui/material";
import { AssignableUserCard } from "./AssignableUserCard";

export const AssignableUserContainer = ({
  users,
  selectedUserId,
  activeAssignableUserIds = [],
  stagedCountByUserId = {},
  onUserSelect,
  onAddAssignableUser,
}: AssignableUserContainerProps) => {
  const { classes, theme } = useStyles();

  const selectedUserName = useMemo(
    () => users.find((u) => u.id === selectedUserId)?.name,
    [users, selectedUserId]
  );

  return (
    <Box className={classes.container}>
      <Typography
        variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
        fontWeight={theme.fontWeight.semiBold}
        color={theme.palette.text.primary}
        noWrap
      >
        Assignable Users:
        {selectedUserName && (
          <Typography
            component="span"
            variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
            fontWeight={theme.fontWeight.semiBold}
            color={theme.palette.primary.main}
          >
            {` Assigning to ${selectedUserName}`}
          </Typography>
        )}
      </Typography>
      <Box className={classes.list}>
        {users.map((user) => (
          <AssignableUserCard
            key={user.id}
            name={user.name ?? ""}
            availableHours={user.availableHours}
            workOrderCount={user.workOrderCount}
            stagedWorkOrderCount={stagedCountByUserId[user.id] ?? 0}
            selected={user.id === selectedUserId}
            showAddButton={!activeAssignableUserIds.includes(user.id)}
            onClick={() => onUserSelect(user.id)}
            onAdd={() => onAddAssignableUser?.(user.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

type User = {
  id: string;
  name?: string;
  availableHours?: number;
  workOrderCount?: number;
};

type AssignableUserContainerProps = {
  users: User[];
  selectedUserId?: string;
  activeAssignableUserIds?: string[];
  stagedCountByUserId?: Record<string, number>;
  onUserSelect: (userId: string) => void;
  onAddAssignableUser?: (userId: string) => void;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  list: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: theme.spacing(2),
    overflow: "auto",
  },
}));
